import { HStack, Text, useBreakpointValue } from '@chakra-ui/react';
import { SensorOrSubSensorInfoWithoutSection } from '@components/cockpit/panels/monitoring/monitoring.types';
import PopupContainer from '@components/map/popup/common/PopupContainer';
import { Switch } from '@components/ui/switch';
import { ToastStatusEnum } from '@components/ui/toaster';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectSensorConfigurationById } from '@redux/situation/monitoring.selectors';
import { useUpdateSensorActivationMutation, useUpdateSensorMaintenanceMutation } from '@services/c2/c2.api';
import { createSensorToast } from '@utils/sensors/sensors.utils';
import { FormattedMessage } from 'react-intl';

type Props = {
  sensorOrSubSensorInfo: SensorOrSubSensorInfoWithoutSection;
  replayMode: boolean;
};

export default function CommonMaintenanceActions({ sensorOrSubSensorInfo, replayMode }: Readonly<Props>) {
  const switchSize = useBreakpointValue<'md' | 'sm'>({ base: 'sm', '4k': 'md' });

  const [patchActivation] = useUpdateSensorActivationMutation();
  const [patchMaintenance] = useUpdateSensorMaintenanceMutation();

  const { configuration, active, maintenance } = sensorOrSubSensorInfo;

  const isSubSensor = 'sensorId' in configuration;
  const parentUniqueKey = isSubSensor ? configuration.sensorId : null;

  const parentConfiguration = useAppSelector((state) =>
    selectSensorConfigurationById(state, replayMode, parentUniqueKey),
  );

  const toggleActive = () => {
    let params;
    if (replayMode) {
      return;
    }
    const newActive = !active;
    if (isSubSensor && parentConfiguration) {
      params = {
        id: configuration.sensorId,
        site: parentConfiguration.site,
        appCode: parentConfiguration.appCode,
        active: newActive,
        type: configuration.type,
        codes: [configuration.code],
      };
    } else {
      params = {
        id: configuration.id,
        site: configuration.site,
        appCode: configuration.appCode,
        active: newActive,
      };
    }
    const toastParams = {
      state: newActive,
      commandValues: { state: newActive },
      command: 'sensors.commands.activationCommand',
      name: configuration.name,
    };
    createSensorToast({
      ...toastParams,
      toast: ToastStatusEnum.INFO,
    });

    patchActivation(params)
      .unwrap()
      .then(() =>
        createSensorToast({
          ...toastParams,
          toast: ToastStatusEnum.SUCCESS,
        }),
      )
      .catch(() =>
        createSensorToast({
          ...toastParams,
          toast: ToastStatusEnum.ERROR,
        }),
      );
  };

  const toggleMaintenance = () => {
    let params;
    if (replayMode) {
      return;
    }
    const newMaintenance = !maintenance;
    if (isSubSensor && parentConfiguration) {
      params = {
        id: configuration.sensorId,
        site: parentConfiguration.site,
        appCode: parentConfiguration.appCode,
        maintenance: newMaintenance,
        type: configuration.type,
        codes: [configuration.code],
      };
    } else {
      params = {
        id: configuration.id,
        site: configuration.site,
        appCode: configuration.appCode,
        maintenance: newMaintenance,
      };
    }
    const toastParams = {
      commandValues: { state: newMaintenance },
      command: 'sensors.commands.maintenanceCommand',
      name: configuration.name,
    };
    createSensorToast({
      ...toastParams,
      toast: ToastStatusEnum.INFO,
    });
    patchMaintenance(params)
      .unwrap()
      .then(() =>
        createSensorToast({
          ...toastParams,
          toast: ToastStatusEnum.SUCCESS,
        }),
      )
      .catch(() =>
        createSensorToast({
          ...toastParams,
          toast: ToastStatusEnum.ERROR,
        }),
      );
  };

  return (
    <>
      <PopupContainer>
        <HStack width="100%" gap="standardXS">
          <Text>
            <FormattedMessage id="sensors.tab.maintenance.connect" />
          </Text>
          <HStack marginLeft="auto" gap={1}>
            <Text textTransform="uppercase" textStyle="screenSm" color="neutral.300">
              <FormattedMessage id={`global.${active ? 'on' : 'off'}`} />
            </Text>
            <Switch size={switchSize} checked={active} onCheckedChange={toggleActive} disabled={replayMode} />
          </HStack>
        </HStack>
      </PopupContainer>
      <PopupContainer>
        <HStack width="100%" gap="standardXS">
          <Text textStyle="screenStd">
            <FormattedMessage id="sensors.tab.maintenance.maintenance" />
          </Text>
          <HStack marginLeft="auto" gap={1}>
            <Text textTransform="uppercase" textStyle="screenSm" color="neutral.300">
              <FormattedMessage id={`global.${maintenance ? 'on' : 'off'}`} />
            </Text>
            <Switch size={switchSize} checked={maintenance} onCheckedChange={toggleMaintenance} disabled={replayMode} />
          </HStack>
        </HStack>
      </PopupContainer>
    </>
  );
}
